import React, { useEffect, useMemo, useState } from "react";
import {
  highlightMatchesPlan,
  exclusivePlanSelection,
  isBundlePlan,
  planCode,
  plansMonthlyTotal,
  selectionHasBundle,
} from "../../Data/billingPlans";
import { CcButton, CcModal } from "../../design-system/carecentre";
import PlanCards from "./PlanCards";
import { useStyles } from "./style";
import useSubscriptionPurchase from "./useSubscriptionPurchase";

const SubscriptionCatalogModal = ({
  open,
  onClose,
  onOpen,
  plans,
  highlightItem,
  onBookDemo,
  busy,
  loading,
}) => {
  const classes = useStyles();
  const { checkoutPlans, beds, busy: purchaseBusy } = useSubscriptionPurchase({
    skipFetch: true,
  });
  const [selectedCodes, setSelectedCodes] = useState([]);
  const waiting = Boolean(busy || purchaseBusy);

  useEffect(() => {
    if (open) onOpen?.();
  }, [open, onOpen]);

  useEffect(() => {
    if (!open) setSelectedCodes([]);
  }, [open]);

  useEffect(() => {
    if (!open || selectedCodes.length > 0) return;
    const match = (plans || []).find((plan) =>
      highlightMatchesPlan(plan, highlightItem),
    );
    if (match?.code && match.selectable !== false && !match.already_granted) {
      setSelectedCodes(exclusivePlanSelection([match.code], plans));
    }
  }, [open, plans, highlightItem, selectedCodes.length]);

  const selectedPlans = useMemo(
    () =>
      (plans || []).filter((plan) =>
        selectedCodes.includes(plan.code),
      ),
    [plans, selectedCodes],
  );
  const monthlyTotal = plansMonthlyTotal(selectedPlans, beds);

  const togglePlan = (plan) => {
    const code = planCode(plan);
    if (!code) return;
    if (isBundlePlan(plan) || code === "all") {
      setSelectedCodes((prev) => (prev.includes(code) ? [] : [code]));
      return;
    }
    setSelectedCodes((prev) => {
      if (selectionHasBundle(prev, plans)) return prev;
      const next = prev.includes(code)
        ? prev.filter((item) => item !== code)
        : [...prev, code];
      return exclusivePlanSelection(next, plans);
    });
  };

  const handleCheckout = () => {
    if (!selectedCodes.length || waiting) return;
    checkoutPlans(exclusivePlanSelection(selectedCodes, plans));
    onClose?.();
  };

  return (
    <CcModal
      open={open}
      onClose={onClose}
      title="Choose a plan"
      subtitle="Choose a package, or pair individual modules with standalone add-ons. An all-modules package already includes everything."
      maxWidth="lg"
      contentClassName={classes.catalogModalBody}
      footer={
        <div className={classes.catalogFooter}>
          <div>
            <span className={classes.catalogFooterLabel}>
              {selectedCodes.length
                ? `${selectedCodes.length} selected · monthly total`
                : "Select at least one plan"}
            </span>
            <strong className={classes.catalogFooterPrice}>
              £{monthlyTotal}
            </strong>
          </div>
          <div className={classes.catalogFooterActions}>
            <CcButton variant="secondary" onClick={onClose}>
              Close
            </CcButton>
            <CcButton
              variant="primary"
              onClick={handleCheckout}
              disabled={waiting || selectedCodes.length === 0}
            >
              Continue
            </CcButton>
          </div>
        </div>
      }
    >
      <PlanCards
        plans={plans}
        highlightItem={highlightItem}
        selectedCodes={selectedCodes}
        onBuy={togglePlan}
        onBookDemo={onBookDemo}
        busy={waiting}
        loading={loading}
      />
    </CcModal>
  );
};

export default SubscriptionCatalogModal;
